import React, { useSyncExternalStore } from 'react';
import { DepthSeriesPoint, TrackCurveHeader } from './logTracks';
import {
  DepthCrosshairState,
  DepthCrosshairStore,
  PlotBox,
  depthToOffset,
  valueAtDepth
} from './depthCrosshair';
import { DepthExtremes } from './useSyncedDepthExtremes';
import styles from './CrosshairOverlay.module.css';

/**
 * Subscribes to the crosshair position.
 *
 * Only leaves may call this. Reading it anywhere above them would re-render the
 * charts at pointer rate — see the note on `createDepthCrosshairStore`.
 */
const useCrosshair = (store: DepthCrosshairStore): DepthCrosshairState =>
  useSyncExternalStore(store.subscribe, store.getSnapshot);

export interface CrosshairLineProps {
  store: DepthCrosshairStore;
  extremes: DepthExtremes;
  plot: PlotBox;
}

/** The one line, spanning every track at the hovered depth. */
export const CrosshairLine: React.FC<CrosshairLineProps> = ({
  store,
  extremes,
  plot
}) => {
  const { depth } = useCrosshair(store);
  if (depth === null) return null;

  return (
    <div
      className={styles.line}
      style={{ top: depthToOffset(depth, extremes, plot) }}
      data-testid="log-crosshair-line"
      aria-hidden="true"
    />
  );
};

export interface CrosshairReadoutGroup {
  trackKey: string;
  trackTitle: string;
  curves: { curve: TrackCurveHeader; points: DepthSeriesPoint[] }[];
}

export interface CrosshairTooltipProps {
  store: DepthCrosshairStore;
  /** One group per track that has values to report, in track order. */
  groups: CrosshairReadoutGroup[];
  /** Label for the depth line, e.g. 'TVDSS'. */
  depthLabel: string;
  /** Half a sample spacing: beyond this the pointer is between samples. */
  tolerance: number;
}

const format = (value: number | null): string =>
  value === null || !Number.isFinite(value) ? '—' : value.toFixed(4);

// Enough to keep the tooltip clear of the cursor without it feeling detached.
const POINTER_GAP = 14;
// Rough size, only used to decide which side of the pointer to sit on. Being a
// little wrong just means flipping slightly early, which is harmless; measuring
// for real would cost a layout read on every frame.
const ESTIMATED_WIDTH = 210;
const ROW_HEIGHT = 15;
const CHROME_HEIGHT = 26;

/**
 * Depth and every curve's value at the pointer, in one tooltip that follows it.
 *
 * One tooltip rather than a chip per track: the question a reader is asking is
 * "what is happening at *this depth*", and the answer spans the tracks. Follows
 * the reference implementation's tooltip placement — offset from the pointer,
 * flipping to the other side near an edge so it is never clipped.
 *
 * Values come from the React point arrays, never `series.points`: Highcharts
 * crops that past `cropThreshold` (300, and there are ~621 rows) so index *i* in
 * the data is not point *i*, and `boost` removes it altogether.
 */
export const CrosshairTooltip: React.FC<CrosshairTooltipProps> = ({
  store,
  groups,
  depthLabel,
  tolerance
}) => {
  const { depth, x, y, clipRight, clipBottom } = useCrosshair(store);
  if (depth === null) return null;

  const rows = groups.reduce((total, group) => total + group.curves.length, 0);
  const estimatedHeight = CHROME_HEIGHT + rows * ROW_HEIGHT;

  const flipX = x + POINTER_GAP + ESTIMATED_WIDTH > clipRight;
  const flipY = y + POINTER_GAP + estimatedHeight > clipBottom;

  return (
    <div
      className={styles.tooltip}
      data-testid="log-crosshair-readout"
      data-flip-x={flipX ? 'true' : 'false'}
      data-flip-y={flipY ? 'true' : 'false'}
      style={{
        left: flipX ? x - POINTER_GAP : x + POINTER_GAP,
        top: flipY ? y - POINTER_GAP : y + POINTER_GAP,
        transform: `translate(${flipX ? '-100%' : '0'}, ${
          flipY ? '-100%' : '0'
        })`
      }}
    >
      <div className={styles.depth} data-testid="log-crosshair-depth">
        {depthLabel} {depth.toFixed(2)}
      </div>
      {groups.map(group => (
        <React.Fragment key={group.trackKey}>
          {group.curves.map(({ curve, points }) => (
            <div
              key={curve.id}
              className={styles.row}
              data-testid={`log-crosshair-value-${curve.id}`}
            >
              <span
                className={styles.swatch}
                style={{ background: curve.color }}
                aria-hidden="true"
              />
              <span className={styles.name}>{curve.label}</span>
              <span className={styles.value}>
                {format(valueAtDepth(points, depth, tolerance))}
              </span>
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};
